import { useEffect, useRef, useState } from "react";

/**
 * Blocks scripts, plugins and form posts even if the sandbox were loosened, and opens links
 * in a new tab rather than inside the frame.
 */
const FRAME_HEAD =
  `<meta http-equiv="Content-Security-Policy" content="script-src 'none'; object-src 'none'; form-action 'none'">` +
  `<base target="_blank">` +
  `<style>body{margin:0;padding:16px;font-family:system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif;` +
  `font-size:14px;line-height:1.5;color:#1f2937;overflow-wrap:anywhere}img{max-width:100%;height:auto}</style>`;

const HEAD_TAG = /<head(\s[^>]*)?>/i;

/**
 * Puts the frame's own head content inside the email's head when it has one, so the email's
 * doctype and rendering mode are kept, and in front of the body otherwise.
 */
const withFrameHead = (html: string): string => {
  const match = HEAD_TAG.exec(html);
  if (!match) return FRAME_HEAD + html;

  const insertAt = match.index + match[0].length;
  return html.slice(0, insertAt) + FRAME_HEAD + html.slice(insertAt);
};

/**
 * Stops a page opened from an email reaching back through window.opener. The frame shares
 * the Portal's origin so that it can be measured, which would otherwise let that page
 * navigate the Portal tab.
 */
const hardenLinks = (doc: Document) => {
  doc.querySelectorAll("a[href]").forEach(link => {
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
  });
};

/**
 * Outlook can send images with the address in alt and no src, so they appear blocked.
 * Restores them, as the legacy tab did.
 */
const restoreOutlookImages = (doc: Document) => {
  doc.querySelectorAll("img").forEach(image => {
    const alt = image.getAttribute("alt") ?? "";
    if (!image.getAttribute("src") && /^https?:\/\//i.test(alt)) {
      image.setAttribute("src", alt);
    }
  });
};

const measure = (doc: Document) =>
  Math.max(doc.documentElement?.scrollHeight ?? 0, doc.body?.scrollHeight ?? 0);

type MailBodyFrameProps = {
  html: string;
  title: string;
};

/**
 * Renders an untrusted HTML email. The sandbox has no allow-scripts, so nothing in the email
 * can run; allow-same-origin is only there so the frame can be sized to its content.
 * Mount with a key per message so each email starts from a fresh frame.
 */
export const MailBodyFrame = ({ html, title }: MailBodyFrameProps) => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const handleLoad = () => {
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;

    hardenLinks(doc);
    restoreOutlookImages(doc);

    const resize = () => setHeight(measure(doc));
    resize();

    observerRef.current?.disconnect();
    observerRef.current = new ResizeObserver(resize);
    if (doc.body) observerRef.current.observe(doc.body);
    doc.querySelectorAll("img").forEach(image => image.addEventListener("load", resize));
  };

  return (
    <iframe
      ref={frameRef}
      title={title}
      srcDoc={withFrameHead(html)}
      sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
      referrerPolicy="no-referrer"
      onLoad={handleLoad}
      className="block w-full border-0"
      style={{ height }}
    />
  );
};
